/**
 * DataPlaneTab — network-scoped view of the unified Data Plane catalog.
 *
 * Replaces the historical "Run -> Input Data" folder tree with a flat,
 * tag-based catalog (see ``packages/ui-shared/components/dataPlane``).
 *
 * Responsibilities:
 *   * Surface the catalog filtered to assets visible to the current network
 *     (``listAssets({ networkId })``).
 *   * Pre-populate the upload dialog's ``defaultMounts`` with the current
 *     network so dropping a file is a one-click "save".
 *   * Pass agent logical ids (from ``network.agents``) and pool ids through
 *     so the mount picker can offer ``@Agent:{network}:{agent}`` / ``@Pool:…``.
 *     Pool ids are loaded from ``GET /networks/{id}/pools`` (customer pools
 *     under ``input/Tenants/{poolId}/``), with ``network.pools`` merged if
 *     present.
 *
 * Org-level write access (``includeOrg``) is intentionally hidden here:
 *   the network workbench is per-network. The org-level Data Plane page
 *   (separate route) is the place to mount globally.
 */
import React, { useEffect, useMemo, useState } from 'react';
import DataPlaneCatalog from '@stepflow/ui-shared/components/dataPlane/DataPlaneCatalog.jsx';
import { listNetworkPools } from '@stepflow/ui-shared/services/agenticNetworksApi.js';

/**
 * Workbench stores ``network.agents`` as a map ``{ [logicalId]: config }`` (see AgentsTab).
 * Older paths may use an array; both must feed the mount picker's @Agent suggestions.
 */
function agentLogicalIdsFromNetwork(agents) {
  if (!agents) return [];
  if (Array.isArray(agents)) {
    return agents
      .map((a) => a?.logicalId || a?.agentName || a?.name)
      .filter((s) => typeof s === 'string' && s.trim().length > 0);
  }
  if (typeof agents === 'object') {
    return Object.keys(agents).filter(
      (k) => typeof k === 'string' && k.trim().length > 0,
    );
  }
  return [];
}

/**
 * Pool ids inlined on ``network`` (rare) or keyed map; primary source is REST
 * ``listNetworkPools`` which matches ``input/Tenants/{poolId}/`` prefixes.
 */
function poolIdsFromNetwork(pools) {
  if (!pools) return [];
  if (Array.isArray(pools)) {
    return pools
      .map((p) => p?.poolId || p?.id)
      .filter((s) => typeof s === 'string' && s.trim().length > 0);
  }
  if (typeof pools === 'object') {
    return Object.keys(pools).filter(
      (k) => typeof k === 'string' && k.trim().length > 0,
    );
  }
  return [];
}

export default function DataPlaneTab({ network }) {
  const networkId = network?.networkId;

  const agentNames = useMemo(
    () => agentLogicalIdsFromNetwork(network?.agents),
    [network?.agents],
  );

  const [poolsFromApi, setPoolsFromApi] = useState([]);

  useEffect(() => {
    if (!networkId || networkId === 'new') {
      setPoolsFromApi([]);
      return undefined;
    }
    let cancelled = false;
    listNetworkPools(networkId)
      .then(({ pools }) => {
        if (cancelled) return;
        const ids = (pools || [])
          .map((p) => p?.poolId || p?.id)
          .filter((s) => typeof s === 'string' && s.trim().length > 0);
        setPoolsFromApi(ids);
      })
      .catch(() => {
        if (!cancelled) setPoolsFromApi([]);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId]);

  const poolIds = useMemo(() => {
    const fromRow = poolIdsFromNetwork(network?.pools);
    return [...new Set([...fromRow, ...poolsFromApi])];
  }, [network?.pools, poolsFromApi]);

  if (!networkId || networkId === 'new') {
    return (
      <div className="flex h-full items-center justify-center bg-gray-50">
        <div className="max-w-md text-center">
          <h2 className="text-base font-semibold text-gray-800">Context unavailable</h2>
          <p className="mt-1 text-xs text-gray-600">
            Deploy this network from the Build tab first. The Context catalogs assets that
            are mounted to a deployed network.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* §2.6b: skills-as-context now folds into the catalog itself as SKILL-lane
          rows (agents are the subscribers) — no separate widget. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        <DataPlaneCatalog
          networkId={networkId}
          agentNames={agentNames}
          poolIds={poolIds}
          includeOrg={false}
          listFilter={{ networkId }}
          skillsCanManage
        />
      </div>
    </div>
  );
}
